import type { SelectProps } from "antd";
import { Empty, Select, Spin } from "antd";
import debounce from "lodash/debounce";
import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

export interface DebounceSelectProps<ValueType = any>
  extends Omit<SelectProps<ValueType | ValueType[]>, "options" | "children"> {
  fetchOptions: (search: string) => Promise<ValueType[]>;
  debounceTimeout?: number;
  isDebounce?: boolean;
}

export interface DebounceSelectRef {
  addOption: (option: any) => void;
}

const DebounceSelect = forwardRef(
  <
    ValueType extends {
      key?: string;
      label: React.ReactNode;
      value: any;
    } = any
  >(
    {
      fetchOptions,
      debounceTimeout = 300,
      isDebounce = true,
      ...props
    }: DebounceSelectProps<ValueType>,
    ref: React.Ref<{ addOption: (option: ValueType) => void }>
  ) => {
    const [fetching, setFetching] = useState(false);
    const [options, setOptions] = useState<ValueType[]>([]);
    const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
    const fetchRef = useRef(0);
    const [inputValue, setInputValue] = useState("");

    const debounceFetcher = useMemo(() => {
      const loadOptions = (value: string) => {
        fetchRef.current += 1;
        const fetchId = fetchRef.current;
        setOptions([]);
        setFetching(true);

        fetchOptions(value).then((newOptions) => {
          if (fetchId !== fetchRef.current) return;
          setOptions(newOptions);
          setFetching(false);
        });
      };

      return debounce(loadOptions, debounceTimeout);
    }, [fetchOptions, debounceTimeout]);

    const handleDropdownVisibleChange = (open: boolean) => {
      if (open && !hasFetchedOnce) {
        setHasFetchedOnce(true);
        setFetching(true);
        fetchOptions("").then((newOptions) => {
          setOptions(newOptions);
          setFetching(false);
        });
      }
    };

    const addOption = (option: ValueType) => {
      setOptions((prev) => {
        const exists = prev.some((opt) => opt.value === option.value);
        return exists ? prev : [...prev, option];
      });
    };

    useImperativeHandle(ref, () => ({
      addOption,
    }));

    return (
      <Select
        filterOption={!isDebounce}
        onSearch={(value) => {
          setInputValue(value);
          if (isDebounce) debounceFetcher(value);
        }}
        showSearch
        searchValue={inputValue}
        value={props.value}
        notFoundContent={
          fetching ? (
            <Spin size="small" />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Sin resultados"
            />
          )
        }
        onDropdownVisibleChange={handleDropdownVisibleChange}
        options={options}
        optionRender={(option) => (
          <div style={{ display: "flex", alignItems: "center" }}>
            {option.label}
          </div>
        )}
        dropdownRender={(menu) => (
          <div>
            {menu}
            <div style={{ textAlign: "center", padding: 8 }}>
              <button
                className="text-text-secondary"
                disabled={fetching}
                onClick={() => {
                  if (!inputValue.trim()) return;
                  setInputValue("");
                  if (isDebounce) debounceFetcher("");
                }}
              >
                Reestablecer búsqueda
              </button>
            </div>
          </div>
        )}
        {...props}
        onChange={(val, option) => {
          if (Array.isArray(val) && val.length > 0) setInputValue("");
          props.onChange?.(val, option);
        }}
      />
    );
  }
);

export default DebounceSelect;